import React, { useState, useCallback } from "react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-slate-600 capitalize">{label.replace(/_/g, " ")}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, disabled, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  />
);

const Textarea = ({ value, onChange, disabled, placeholder }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    rows={3}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
  />
);

const NumberInput = ({ value, onChange, disabled, min, max }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    disabled={disabled}
    min={min}
    max={max}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  />
);

const SelectInput = ({ value, onChange, disabled, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#096dea]/30 disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? "bg-[#096dea]" : "bg-slate-200"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

// const ColorInput = ({ value, onChange, disabled }) => (
//   <div className="flex items-center gap-2">
//     <input
//       type="color"
//       value={value || "#000000"}
//       onChange={(e) => onChange(e.target.value)}
//       disabled={disabled}
//       className="h-9 w-9 cursor-pointer rounded-lg border border-slate-200 p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
//     />
//     <input
//       type="text"
//       value={value}
//       onChange={(e) => onChange(e.target.value)}
//       disabled={disabled}
//       className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
//     />
//   </div>
// );

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <h4 className="text-sm font-semibold text-slate-950">{title}</h4>
    <p className="mt-1 text-xs text-slate-500">{description}</p>
  </div>
);

const SaveButton = ({ saving, onClick, disabled }) => (
  <div className="pt-2 flex items-center gap-3">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || saving}
      className="rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {saving ? "Saving..." : "Save changes"}
    </button>
  </div>
);

const getConfig = (configs, key) => configs?.find((c) => c.config_key === key);

// --- Business Profile ---
const BusinessProfileSection = ({ configs, disabled, onSave }) => {
  const existing = getConfig(configs, "business_profile");
  const [fields, setFields] = useState(() => ({
    description: "",
    address: "",
    website: "",
    support_email: "",
    support_phone: "",
    ...(existing?.config_json || {}),
  }));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("business_profile", fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title="General Information" description="General business information and contact details." />
      <div className="space-y-3">
        <Field label="Business Description">
          <Textarea value={fields.description} onChange={(v) => setFields((p) => ({ ...p, description: v }))} disabled={disabled} placeholder="Describe the business..." />
        </Field>
        <Field label="Address">
          <TextInput value={fields.address} onChange={(v) => setFields((p) => ({ ...p, address: v }))} disabled={disabled} placeholder="123 Main St, City, Country" />
        </Field>
        <Field label="Website">
          <TextInput value={fields.website} onChange={(v) => setFields((p) => ({ ...p, website: v }))} disabled={disabled} placeholder="https://example.com" />
        </Field>
        <Field label="Support Email">
          <TextInput value={fields.support_email} onChange={(v) => setFields((p) => ({ ...p, support_email: v }))} disabled={disabled} placeholder="support@example.com" />
        </Field>
        <Field label="Support Phone">
          <TextInput value={fields.support_phone} onChange={(v) => setFields((p) => ({ ...p, support_phone: v }))} disabled={disabled} placeholder="+1 (555) 000-0000" />
        </Field>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- Business Hours ---
const BusinessHoursSection = ({ configs, disabled, onSave }) => {
  const existing = getConfig(configs, "business_hours");
  const [fields, setFields] = useState(() => {
    const defaults = { timezone: "" };
    DAYS.forEach((d) => { defaults[d] = { enabled: false, open: "", close: "" }; });
    return { ...defaults, ...(existing?.config_json || {}) };
  });
  const [saving, setSaving] = useState(false);

  const updateDay = (day, key, value) => {
    setFields((p) => ({ ...p, [day]: { ...p[day], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("business_hours", fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title="Operating Hours" description="Set operating hours for each day of the week." />
      <div className="space-y-2">
        <div className="grid grid-cols-[100px_50px_1fr_1fr] gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
          <span>Day</span>
          <span>On</span>
          <span>Open</span>
          <span>Close</span>
        </div>
        {DAYS.map((day) => (
          <div key={day} className="grid grid-cols-[100px_50px_1fr_1fr] gap-2 items-center">
            <span className="text-sm font-medium text-slate-700">{DAY_LABELS[day]}</span>
            <Toggle checked={fields[day]?.enabled || false} onChange={(v) => updateDay(day, "enabled", v)} disabled={disabled} />
            <input
              type="time"
              value={fields[day]?.open || ""}
              onChange={(e) => updateDay(day, "open", e.target.value)}
              disabled={disabled || !fields[day]?.enabled}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <input
              type="time"
              value={fields[day]?.close || ""}
              onChange={(e) => updateDay(day, "close", e.target.value)}
              disabled={disabled || !fields[day]?.enabled}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        ))}
        <div className="pt-2">
          <Field label="Timezone">
            <TextInput value={fields.timezone} onChange={(v) => setFields((p) => ({ ...p, timezone: v }))} disabled={disabled} placeholder="America/New_York" />
          </Field>
        </div>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- AI Settings ---
const AiSettingsSection = ({ configs, disabled, onSave }) => {
  const existing = getConfig(configs, "ai_settings");
  const [fields, setFields] = useState(() => ({
    tone: "neutral",
    language: "en",
    fallback_message: "",
    max_response_length: 500,
    enable_citations: false,
    ...(existing?.config_json || {}),
  }));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("ai_settings", fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title="AI Tone & Behavior" description="Configure AI behaviour, tone, and response parameters." />
      <div className="space-y-3">
        <Field label="Response Tone">
          <SelectInput value={fields.tone} onChange={(v) => setFields((p) => ({ ...p, tone: v }))} disabled={disabled} options={["Friendly", "Professional", "Direct"]} />
        </Field>
        <Field label="Language">
          <TextInput value={fields.language} onChange={(v) => setFields((p) => ({ ...p, language: v }))} disabled={disabled} placeholder="en" />
        </Field>
        <Field label="Fallback Message">
          <TextInput value={fields.fallback_message} onChange={(v) => setFields((p) => ({ ...p, fallback_message: v }))} disabled={disabled} placeholder="I could not find an answer to that." />
        </Field>
        <Field label="Max Response Length">
          <NumberInput value={fields.max_response_length} onChange={(v) => setFields((p) => ({ ...p, max_response_length: v }))} disabled={disabled} min={50} max={4000} />
        </Field>
        <div className="flex items-center gap-3">
          <Toggle checked={!!fields.enable_citations} onChange={(v) => setFields((p) => ({ ...p, enable_citations: v }))} disabled={disabled} />
          <span className="text-sm text-slate-700">Enable citations in responses</span>
        </div>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- Notification Settings (commented out) ---
// --- Branding (commented out) ---

// --- Main Component ---
const TenantConfigTab = ({ configs, disabled, onSave }) => {
  const handleSave = useCallback(
    async (configKey, configJson) => {
      if (onSave) await onSave(configKey, configJson);
    },
    [onSave]
  );

  return (
    <div className="space-y-6">
      <BusinessProfileSection configs={configs} disabled={disabled} onSave={handleSave} />
      <BusinessHoursSection configs={configs} disabled={disabled} onSave={handleSave} />
      <AiSettingsSection configs={configs} disabled={disabled} onSave={handleSave} />

    </div>
  );
};

export default TenantConfigTab;
